import { AxiosResponse } from 'axios';
import getConfig from 'next/config';
import router from 'next/router';
import { ReactElement, useEffect } from 'react';

import { GymStatus } from '@tgg/common-types';
import { axiosInstance } from '@tgg/micro-services/axios';
import { GymDisplayNamesResponse, SummarisedBranch } from '@tgg/services';
import { GymLookup, Heading, useFlagsContext } from '@tgg/ui';
import {
    GsspWithLoggerParameters,
    OptimizelyEvents,
    gsspWithLogger,
} from '@tgg/util';

import { applicationIdentifier } from 'apps/commerce/core/constants';

import { JoinJourneyLayout } from '../../../components';
import {
    ShoppingBasketCookie,
    getShoppingBasketCookie,
    setupShoppingBasketCookie,
    updateShoppingBasketCookie,
} from '../../../core/cookies';
import { LocalConfig } from '../../../next.config.types';
import { getMappedVisitedGyms } from '../utils/getMappedVisitedGyms';

import { StyledContainer } from './FindYourGym.styled';
import { FindYourGymData } from './FindYourGym.types';

const handleSelect = async (gym: SummarisedBranch) => {
    const shoppingBasket = getShoppingBasketCookie() as ShoppingBasketCookie;
    updateShoppingBasketCookie({
        ...shoppingBasket,
        branchId: gym.BranchId,
        branchName: gym.Name,
        // Avoids pending 9000 cases
        // Retained so that BE can re-use the previously generated memberID.
        memberId: shoppingBasket?.memberId,
        transactionId: shoppingBasket?.transactionId,
    });
    await getMappedVisitedGyms(gym);
    await router.push('/join-now/choose-membership/');
};

export function FindYourGym({ data }: FindYourGymData) {
    setupShoppingBasketCookie({ force: false });

    const { trackFlagsEvent } = useFlagsContext();

    useEffect(() => {
        trackFlagsEvent(OptimizelyEvents.VISIT_FIND_YOUR_GYM);
    }, []);

    return (
        <StyledContainer>
            <Heading>Find your gym</Heading>
            <GymLookup listOfGyms={data} onSelect={handleSelect} />
        </StyledContainer>
    );
}

FindYourGym.getLayout = function getLayout(page: ReactElement) {
    return <JoinJourneyLayout>{page}</JoinJourneyLayout>;
};

const pageParameters: GsspWithLoggerParameters = {
    applicationIdentifier,
    meta: {
        journeyInfo: {
            journeyName: 'JoinJourney',
            journeyStep: 'FindYourGym',
        },
    },
};

export const getServerSideProps = gsspWithLogger<FindYourGymData>(
    async (_context, injectorParameters) => {
        const { isMobileSession, loggerParameters, logger } =
            injectorParameters;

        const {
            publicRuntimeConfig: { APP_BASE_PATH },
            serverRuntimeConfig: { APP_INTERNAL_BASE_URL },
        }: LocalConfig = getConfig();

        const domain = `${APP_INTERNAL_BASE_URL}${APP_BASE_PATH}`;
        const gymDisplayNamesProxyRouteUrl = `${domain}/api/proxy/gyms/displaynames/`;

        logger.debug(
            `ClassesGymsList getServerSideProps - about to call ${gymDisplayNamesProxyRouteUrl} to get gyms`,
            loggerParameters,
        );

        const { data }: AxiosResponse<GymDisplayNamesResponse[]> =
            await axiosInstance.get(gymDisplayNamesProxyRouteUrl, {
                params: {
                    statuses: [
                        GymStatus.Open,
                        GymStatus.ComingSoon,
                        GymStatus.Closed,
                    ],
                },
            });

        const mappedGyms = data.map(gym => {
            return {
                BranchId: gym.branchId,
                Name: gym.displayName,
            };
        });

        const robotsMetaData = {
            robotsMetaData: 'noindex, nofollow',
        };

        return {
            props: {
                data: mappedGyms,
                schema: robotsMetaData,
            },
        };
    },
    pageParameters,
);

export default FindYourGym;
